import { useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { getCanonicalUrl } from "@/lib/canonical";

/**
 * Canonicalization component - handles all URL normalization:
 * - Removes trailing slash (except homepage)
 * - Lowercases paths
 * - Removes index.html
 * - Strips utm_* and ref query params
 * - Keeps only whitelisted params (page)
 */
const TrailingSlashRedirect = () => {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Only canonicalize path/search, ignore domain in preview environments
    const currentPath = location.pathname;
    let needsRedirect = false;
    let newPath = currentPath;
    
    // Remove index.html
    if (currentPath.includes('index.html')) {
      newPath = currentPath.replace(/\/index\.html$/i, '/').replace(/index\.html$/i, '/');
      needsRedirect = true;
    }
    
    // Lowercase path
    if (newPath !== newPath.toLowerCase()) {
      newPath = newPath.toLowerCase();
      needsRedirect = true;
    }
    
    // Remove trailing slash (except root)
    if (newPath !== '/' && newPath.endsWith('/')) {
      newPath = newPath.slice(0, -1);
      needsRedirect = true;
    }
    
    // Filter query params - remove utm_* and ref
    const searchParams = new URLSearchParams(location.search);
    const allowedParams = new URLSearchParams();
    let searchChanged = false;
    
    searchParams.forEach((value, key) => {
      if (key.startsWith('utm_') || key === 'ref') {
        searchChanged = true;
      } else if (key === 'page') {
        allowedParams.append(key, value);
      }
    });
    
    const newSearch = allowedParams.toString() ? `?${allowedParams.toString()}` : '';
    if (newSearch !== location.search) {
      searchChanged = true;
    }
    
    // Only redirect if changes are needed
    if (needsRedirect || searchChanged) {
      const redirectTo = newPath + newSearch + location.hash;
      navigate(redirectTo, { replace: true });
    }
  }, [location.pathname, location.search, location.hash, navigate]);

  return null;
};

export default TrailingSlashRedirect;
